import React, { useContext, useEffect, useMemo, useState } from "react";
import { Col, Container, Form, Row } from "react-bootstrap";
import Toggle from "react-toggle";
import CalculatorContext, { CalculatorProvider } from "./CalculatorContext";
import { formatAsCurrency } from "./utils/text-utils";
import "bootstrap/dist/css/bootstrap.min.css";
import "react-toggle/style.css";
import "./App.css";

const App = () => {
  return (
    <CalculatorProvider>
      <Content />
    </CalculatorProvider>
  );
};

const Content = () => {
  const {
    contextData,
    setContextData,
    country,
    products,
    getProduct,
    isEnabled,
    enableProduct,
    disableProduct,
    updateData,
  } = useContext(CalculatorContext);
  const [isMonthlyTotalVisible, setIsMonthlyTotalVisible] = useState(false);
  const [isMonthlyDiscount, setIsMonthlyDiscount] = useState(false);
  const [settings, setSettings] = useState({
    limited_agent: false,
    pdf_form: false,
  });

  const onMonthlyDiscountToggled = (e) => {
    setIsMonthlyDiscount(e.target.checked);
  };

  const onEngagePricingOptionChange = (e) => {
    setContextData("engage_pricing", e.target.value);
    setContextData("engage_phone_system_additional_line", false); // Always toggle off when option is changed

    const isLegacyVisible = e.target.value === "legacy";
    updateData("engage_legacy", "hidden", !isLegacyVisible);
    updateData("engage_phone_system", "hidden", isLegacyVisible);
    updateData("engage_phone_system_additional_line", "hidden", true); // Always hide when option is changed

    // Always disable products when option is changed
    disableProduct({ id: "engage_legacy" });
    disableProduct({ id: "engage_phone_system" });
    disableProduct({ id: "engage_phone_system_additional_line" });
  };

  const onEngageAdditionalLineSettingToggled = (e) => {
    const name = "engage_phone_system_additional_line";
    const { checked } = e.target;
    updateData(name, "hidden", !checked);
    setContextData(name, checked);

    const product = getProduct(name);
    if (checked) {
      const engageProduct = getProduct("engage_phone_system");
      if (isEnabled(engageProduct)) {
        enableProduct(product);
      }
    } else {
      disableProduct(product);
    }
  };

  const onSettingToggled = (name) => (e) => {
    const { checked } = e.target;
    updateData(name, "hidden", !checked);
    setSettings((settings) => ({
      ...settings,
      [name]: checked,
    }));
  };

  return (
    <Container>
      <div>
        <img className="logo" src={require("./assets/logo.webp")} />
      </div>
      <div className="app">
        <Row>
          <Col xs={7}>
            {products.map((product) => (
              <Product
                key={`${country}-${product.id}`}
                data={product}
                isMonthlyTotalVisible={isMonthlyTotalVisible}
              />
            ))}
          </Col>
          <Col xs={1} />
          <Col xs={4}>
            {isMonthlyTotalVisible && (
              <MonthlyTotal
                isMonthlyDiscount={isMonthlyDiscount}
                onMonthlyDiscountToggled={onMonthlyDiscountToggled}
              />
            )}
            <AnnualTotal isMonthlyDiscount={isMonthlyDiscount} />
            <div className="monthly-switch-container">
              <Toggle
                icons={{
                  checked: (
                    <div
                      className="monthly-switch-icon"
                      style={{ marginLeft: 2 }}
                    >
                      M
                    </div>
                  ),
                  unchecked: <div className="monthly-switch-icon">A</div>,
                }}
                checked={isMonthlyTotalVisible}
                onChange={() =>
                  setIsMonthlyTotalVisible(!isMonthlyTotalVisible)
                }
              />
            </div>
          </Col>
        </Row>
      </div>
      <div className="footer-container">
        <div className="setting-item-container" style={{ marginTop: 24 }}>
          <div className="form-select-label">Engage pricing</div>
          <Form.Select
            value={contextData.engage_pricing}
            onChange={onEngagePricingOptionChange}
            className="form-select-option"
          >
            {ENGAGE_PRICING_OPTIONS.map((e) => (
              <option value={e.value}>{e.label}</option>
            ))}
          </Form.Select>
        </div>

        {contextData.engage_pricing === "current" && (
          <div className="setting-item-container">
            <div className="setting-item-text">Show Engage additional line</div>
            <Toggle
              icons={false}
              checked={contextData["engage_phone_system_additional_line"]}
              onChange={onEngageAdditionalLineSettingToggled}
            />
          </div>
        )}

        <div className="setting-item-container">
          <div className="setting-item-text" style={{ marginRight: 28 }}>
            Show limited agent users
          </div>
          <Toggle
            icons={false}
            checked={settings["limited_agent"]}
            onChange={onSettingToggled("limited_agent")}
          />
        </div>

        <div className="setting-item-container">
          <div className="setting-item-text" style={{ marginRight: 61 }}>
            Show PDF form filler
          </div>
          <Toggle
            icons={false}
            checked={settings["pdf_form"]}
            onChange={onSettingToggled("pdf_form")}
          />
        </div>
      </div>
    </Container>
  );
};

const Product = ({ data = {}, isMonthlyTotalVisible }) => {
  const {
    id,
    title,
    entity_name,
    base,
    base_licenses,
    min_licenses,
    per_license,
    hidden,
  } = data;

  const calculator = useContext(CalculatorContext);
  const [hovered, setHovered] = useState(false);
  const [numOfLicenses, setNumOfLicenses] = useState(min_licenses);
  const [isBasePriceEditable, setIsBasePriceEditable] = useState(false);
  const [isPerLicensePriceEditable, setIsPerLicensePriceEditable] =
    useState(false);

  const enabled = calculator.isEnabled(data);
  const fleetTrackingEnabled = calculator.isEnabled({ id: "fleet_tracking" });
  const fleetCamerasEnabled = calculator.isEnabled({ id: "fleet_cameras" });

  const [isProductPriceVisible, setIsProductPriceVisible] = useState(
    enabled && !per_license
  );
  const [total, setTotal] = useState(enabled && !per_license ? base : 0);

  useEffect(() => {
    if (enabled) {
      const monthlyTotal = per_license
        ? base + numOfLicenses * per_license
        : base;
      const total = isMonthlyTotalVisible ? monthlyTotal : monthlyTotal * 12;
      setTotal(total);
    } else {
      setNumOfLicenses(min_licenses);
      setIsProductPriceVisible(false);
    }
  }, [enabled, isMonthlyTotalVisible]);

  const getSubtitleComponents = (type = "perLicense", data, onClick) => {
    const { id, subtitle: subtitlePerLicense, subtitleBase } = data;
    const subtitle = type === "perLicense" ? subtitlePerLicense : subtitleBase;
    if (!subtitle) {
      return "";
    }

    const onPriceInputChange = (value) => {
      onPriceChange(id, type, value);
    };

    if (typeof subtitle === "string") {
      return subtitle;
    } else if (typeof subtitle === "function") {
      let res = subtitle(data);

      // Just a simple tag parser
      if (res.includes("<input>")) {
        if (
          (type === "perLicense" && isPerLicensePriceEditable) ||
          (type === "base" && isBasePriceEditable)
        ) {
          const components = [];
          const items = res.split("<input>").filter((e) => e.length > 0);

          items.forEach((text, i) => {
            const idx = text.indexOf("</input");
            if (idx === -1) {
              components.push(text);
            } else {
              const price = text.substring(1, idx);
              components.push(
                <PriceInput
                  defaultValue={price}
                  onChange={onPriceInputChange}
                />
              );
            }

            let nextText = text.substring(idx + "</input>".length);
            if (nextText.includes("<small>")) {
              const smallItems = nextText
                .replace("</small>", "")
                .split("<small>");
              nextText = (
                <div style={{ marginLeft: 8 }}>
                  {smallItems[0]} <small>{smallItems[1]}</small>
                </div>
              );
            } else {
              nextText = <div style={{ marginLeft: 8 }}>{nextText}</div>;
            }
            components.push(nextText);
          });

          res = (
            <div
              style={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
              }}
            >
              {components.map((e) => e)}
            </div>
          );
        } else {
          res = res.replaceAll("<input>", "").replaceAll("</input>", "");
          if (res.includes("<small>")) {
            const smallItems = res.replace("</small>", "").split("<small>");
            res = (
              <div onClick={onClick}>
                {smallItems[0]} <small>{smallItems[1]}</small>
              </div>
            );
          } else {
            res = <div onClick={onClick}>{res}</div>;
          }
        }
      }

      return res;
    }
  };

  const getSubtitleText = (data) => {
    const basePriceSubtitle = getSubtitleComponents("base", data, () =>
      setIsBasePriceEditable(true)
    );
    const perLicensePriceSubtitle = getSubtitleComponents(
      "perLicense",
      data,
      () => setIsPerLicensePriceEditable(true)
    );

    if (id === "engage_legacy") {
      return (
        <h6 className="subtitle">
          <div
            style={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
            }}
          >
            {basePriceSubtitle}
            {!!basePriceSubtitle && <div style={{ width: 4 }} />}
            {perLicensePriceSubtitle}
          </div>
        </h6>
      );
    } else if (id === "operator_ai_minute") {
      const price = numOfLicenses * per_license;
      return (
        <>
          <h6 className="product-subtitle">
            {formatAsCurrency(price, true)} per month
          </h6>
          <h6 className="product-subtitle">{perLicensePriceSubtitle}</h6>
        </>
      );
    }
    return (
      <>
        {!!basePriceSubtitle && (
          <h6 className="product-subtitle">{basePriceSubtitle}</h6>
        )}
        {!!perLicensePriceSubtitle && (
          <h6 className="product-subtitle">{perLicensePriceSubtitle}</h6>
        )}
      </>
    );
  };

  const onPriceChange = (
    id,
    type,
    value,
    _base = base,
    _perLicense = per_license
  ) => {
    let baseValue;
    let perLicenseValue;

    if (type === "perLicense") {
      baseValue = _base != null ? _base : base;
      perLicenseValue = value;
      calculator.updateData(id, "per_license", perLicenseValue);
    } else {
      baseValue = value;
      perLicenseValue = _perLicense != null ? _perLicense : per_license;
      calculator.updateData(id, "base", baseValue);
    }

    const monthlyTotal = baseValue + numOfLicenses * perLicenseValue;
    const total = isMonthlyTotalVisible ? monthlyTotal : monthlyTotal * 12;
    setTotal(total);
    setIsBasePriceEditable(false);
    setIsPerLicensePriceEditable(false);
  };

  const onQuantityChange = (value) => {
    // handle internal state
    const count = value <= base_licenses ? 0 : value - base_licenses;
    setNumOfLicenses(count);

    const monthlyTotal = base + count * per_license;
    const total = isMonthlyTotalVisible ? monthlyTotal : monthlyTotal * 12;
    setTotal(total);

    // update calculator state
    calculator.updateLicenseCount(id, count);
  };

  const onEnabledChange = (e) => {
    if (e.target.checked) {
      setNumOfLicenses(min_licenses);
      calculator.enableProduct(data);

      if (!per_license) {
        // Calculate and show the price immediately because we don't need to set the number of items first
        const monthlyTotal = base;
        const total = isMonthlyTotalVisible ? monthlyTotal : monthlyTotal * 12;
        setTotal(total);
        setIsProductPriceVisible(true);
      }

      if (id === "engage_phone_system") {
        // Automatically enable "engage_phone_system_additional_line"
        const additionalLineProduct = calculator.getProduct(
          "engage_phone_system_additional_line"
        );
        calculator.enableProduct(additionalLineProduct);
      }
    } else {
      setNumOfLicenses(min_licenses);
      calculator.disableProduct(data);
      setIsProductPriceVisible(false);

      if (id === "engage_phone_system") {
        // Automatically disable "engage_phone_system_additional_line"
        calculator.disableProduct({
          id: "engage_phone_system_additional_line",
        });
      }
    }
  };

  if (
    // prettier-ignore
    hidden ||
    (id === "fleet_management" && (fleetTrackingEnabled || fleetCamerasEnabled)) ||
    (id === "fleet_tracking" && !enabled && !fleetCamerasEnabled) ||
    (id === "fleet_cameras" && !enabled && !fleetTrackingEnabled)
  ) {
    // When Fleet Management is enabled, it will become invisible, and instead,
    // both Fleet Tracking and Fleet Cameras will become visible.
    // See calculator.enableProduct() for additional info.
    return null;
  }
  return (
    <Row
      className={`item-container ${enabled ? "opacity-100" : ""}`}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        marginTop:
          id === "engage_phone_system_additional_line" ? -28 : undefined,
      }}
    >
      <Col
        xs={1}
        className="pt-2"
        style={{
          marginTop:
            id === "engage_phone_system" || id === "operator_ai_minute"
              ? 34
              : undefined,
        }}
      >
        {hovered && id !== "engage_phone_system_additional_line" && (
          <Toggle icons={false} checked={enabled} onChange={onEnabledChange} />
        )}
      </Col>
      <Col>
        <Row
          className="product-pricing"
          style={{
            paddingTop:
              id === "engage_phone_system_additional_line" ? 5 : undefined,
          }}
        >
          <Col xs={6}>
            {!!title && <h4 className="header">{title}</h4>}
            {getSubtitleText(data)}
          </Col>
          <Col
            style={{
              paddingLeft: 24,
              marginTop:
                id === "engage_phone_system" || id === "operator_ai_minute"
                  ? 38
                  : id === "engage_phone_system_additional_line"
                  ? -10
                  : undefined,
            }}
          >
            {per_license > 0 && enabled && (
              <QuantityInput
                entityName={entity_name}
                minValue={
                  id === "operator_ai_minute"
                    ? 30
                    : base_licenses + min_licenses
                }
                value={base_licenses + numOfLicenses}
                onChange={onQuantityChange}
                onQuantityAvailable={() => setIsProductPriceVisible(true)}
              />
            )}
          </Col>
          <div
            className={
              "item-price subtitle text-end text-black " +
              (!enabled && total === 0 ? "opacity-25" : "opacity-100")
            }
            style={{
              marginTop:
                id === "engage_phone_system" || id === "operator_ai_minute"
                  ? 38
                  : id === "engage_phone_system_additional_line"
                  ? -10
                  : undefined,
            }}
          >
            {isProductPriceVisible &&
              formatAsCurrency(
                id === "premium_support"
                  ? isMonthlyTotalVisible
                    ? calculator.premiumSupportPrice
                    : calculator.premiumSupportPrice * 12
                  : total,
                true
              )}
          </div>
        </Row>
      </Col>
    </Row>
  );
};

const PriceInput = ({ defaultValue, onChange }) => {
  const [value, setValue] = useState(defaultValue);

  const onFocus = (e) => {
    e.target.select();
  };

  const onBlur = (e) => {
    const { value } = e.currentTarget;
    setValue(value);
    onChange(parseFloat(value));
  };

  const onEnterPress = (e) => {
    if (e.keyCode === 13) {
      onBlur(e);
    }
  };

  return (
    <Form.Control
      autoFocus
      onFocus={onFocus}
      onBlur={onBlur}
      onKeyDown={onEnterPress}
      type="number"
      min={0}
      defaultValue={value}
      style={{ width: 88 }}
    />
  );
};

const QuantityInput = ({
  entityName,
  minValue,
  value,
  disabled,
  onChange,
  onQuantityAvailable,
}) => {
  const [isInitial, setIsInitial] = useState(true);
  const [isEditMode, setIsEditMode] = useState(false);

  const onInitialTextClick = () => {
    if (!disabled) {
      setIsInitial(false);
      setIsEditMode(true);
    }
  };

  const onTextClick = () => {
    if (!disabled) {
      setIsEditMode(true);
    }
  };

  const onFocus = (e) => {
    e.target.select();
  };

  const onBlur = (e) => {
    let value = parseFloat(e.currentTarget.value);
    if (value < minValue) {
      value = minValue;
    }

    onChange(value);
    onQuantityAvailable(true);
    setIsEditMode(false);
  };

  const onEnterPress = (e) => {
    if (e.keyCode === 13) {
      onBlur(e);
    }
  };

  if (isInitial) {
    return (
      <div
        className={
          "enter-quantity-text " + (disabled ? "opacity-25" : "opacity-100")
        }
        onClick={onInitialTextClick}
      >
        Enter number of {entityName}s
      </div>
    );
  } else if (isEditMode) {
    return (
      <Form.Control
        autoFocus
        onFocus={onFocus}
        onBlur={onBlur}
        onKeyDown={onEnterPress}
        type="number"
        min={minValue}
        max={10000}
        defaultValue={value}
        style={{ width: 120 }}
      />
    );
  }
  return (
    <div
      className={
        "item-quantity-text " + (disabled ? "opacity-25" : "opacity-100")
      }
      onClick={onTextClick}
    >
      {value} {entityName}
      {value === 1 ? "" : "s"}
    </div>
  );
};

const DiscountInput = () => {
  const { discount, setDiscount } = useContext(CalculatorContext);
  const [isEditMode, setIsEditMode] = useState(false);

  const onFocus = (e) => {
    e.target.select();
  };

  const onBlur = (e) => {
    setDiscount(parseFloat(e.currentTarget.value));
    setIsEditMode(false);
  };

  const onEnterPress = (e) => {
    if (e.keyCode === 13) {
      setDiscount(parseFloat(e.currentTarget.value));
      setIsEditMode(false);
    }
  };

  if (isEditMode) {
    return (
      <Form.Control
        autoFocus
        onFocus={onFocus}
        onBlur={onBlur}
        onKeyDown={onEnterPress}
        type="number"
        min={0}
        max={100}
        defaultValue={discount}
        placeholder={discount}
      />
    );
  }
  return (
    <h5 className="header text-success" onClick={() => setIsEditMode(true)}>
      {discount}% Discount
    </h5>
  );
};

const MonthlyTotal = ({ isMonthlyDiscount, onMonthlyDiscountToggled }) => {
  const { monthly, monthlyDiscounted, discount, savings } =
    useContext(CalculatorContext);
  const [discountSwitchVisible, setDiscountSwitchVisible] = useState(false);

  return (
    <div>
      <Row className="align-items-center">
        <Col>
          <h4
            className="header"
            onDoubleClick={() =>
              setDiscountSwitchVisible(!discountSwitchVisible)
            }
          >
            Annual, Paid Monthly
          </h4>
        </Col>
        {isMonthlyDiscount && (
          <Col xs="auto">
            <DiscountInput />
          </Col>
        )}
      </Row>
      {discountSwitchVisible && (
        <Row className="mt-1 mb-1">
          <Col md="auto">
            <Toggle
              icons={false}
              checked={isMonthlyDiscount}
              onChange={onMonthlyDiscountToggled}
            />
          </Col>
          <Col style={{ marginLeft: -16 }}>
            <div className="monthly-discount-label">Monthly discount</div>
          </Col>
        </Row>
      )}
      <div className="total-container mb-32px">
        {isMonthlyDiscount && discount > 0 && (
          <div className="struckthrough-monthly-container">
            <h4 className="struckthrough-monthly">
              {formatAsCurrency(monthly)}
            </h4>
            <span className="per-month-small"> / month</span>
          </div>
        )}
        <h2 className="header text-center">
          {formatAsCurrency(isMonthlyDiscount ? monthlyDiscounted : monthly)}
          <span className="per-month-year"> / month</span>
        </h2>
        {isMonthlyDiscount && (
          <h6 className="text-success text-center mt-3">
            ({formatAsCurrency(savings / 12)} in savings per month)
          </h6>
        )}
      </div>
    </div>
  );
};

const AnnualTotal = ({ isMonthlyDiscount }) => {
  const { country, annual, savings } = useContext(CalculatorContext);

  return (
    <div>
      <Row className="align-items-center">
        <Col>
          <h4 className="header">Annual, Paid Upfront</h4>
        </Col>
        {!isMonthlyDiscount && (
          <Col xs="auto">
            <DiscountInput />
          </Col>
        )}
      </Row>
      <div className="total-container">
        <h2 className="header text-center mb-0">
          {formatAsCurrency(annual)}
          <span className="per-month-year"> / year</span>
        </h2>
        <h6 className="text-success text-center mt-3">
          ({formatAsCurrency(savings)} in savings per year)
        </h6>
      </div>
      {country === "aus" && (
        <h6 className="text-danger text-center mt-2">
          **Pricing Excludes GST**
        </h6>
      )}
    </div>
  );
};

const ENGAGE_PRICING_OPTIONS = [
  { label: "Current", value: "current" },
  { label: "Legacy", value: "legacy" },
];

export default App;
